/* eslint-disable react/no-array-index-key */

import React from 'react';
import isArray from 'lodash/isArray';
import { uid } from 'react-uid';

import Cta from 'core/components/Cta';
import { ICONS } from 'core/components/Icon/Icon';
import { JssTextType, isJssField } from 'core/utils/sitecore-utils';
import * as Typo from 'core/styles/Typography';

import * as S from './FormReview.style';

export type Content = {
	/** Form field label */
	label: JssTextType | React.ReactNode;
	/** Captured info */
	info: string[] | string | number;
};

interface IFormReview {
	sections: {
		/** Title of the review section, usually align with the step title */
		title: JssTextType;
		/** Content of the review section */
		contents: Content[];
		/** Link of the cta that use to direct the users to a different page (step) */
		ctaLink: JssTextType;
		/** Callback to handle the click of link */
		ctaCallback?: () => void;
		/** Flag if Edit button should align on the label and value on the first row */
		isEditAlignToLabel?: boolean;
		/** Flag if Edit button should align on the label and value on the first row */
		isEditCtaVisible?: boolean;
		/** Flag if header element should be h4 or h3 */
		isHeaderH3?: boolean;
	}[];
}

const FormReview = ({ sections }: IFormReview) => (
	<S.Wrapper>
		{sections.map(
			({
				ctaLink,
				ctaCallback,
				contents,
				title,
				isEditAlignToLabel = false,
				isEditCtaVisible = true,
				isHeaderH3 = false,
			}) =>
				isEditAlignToLabel ? (
					<S.Section key={title?.value ?? ''}>
						<S.DlEdit>
							{contents.map(({ label, info }, contentIndex) => {
								const isJss = isJssField(label);

								return (
									<>
										<div>
											<div key={uid(`${info ?? ''}_${contentIndex}`)}>
												<dt>
													{isJss ? (
														<Typo.Body
															color="subdued"
															jss={label as JssTextType}
															margin="0"
															isBold
														/>
													) : (
														<div>{label ?? ''}</div>
													)}
												</dt>
												<dd>
													{/* NOTE: use index as key for the array is static */}
													{(isArray(info) ? info : [info]).map(
														(value, index) => (
															<Typo.Body margin="0" key={index} isBold>
																{value ?? ''}
															</Typo.Body>
														),
													)}
												</dd>
											</div>
											<S.CtaContainerEdit>
												{contentIndex === 0 && ctaLink && isEditCtaVisible && (
													<Cta
														icon={ICONS.EDIT}
														onClick={ctaCallback}
														ctaStyle="plain"
														isWithoutPadding
														ctaType="button"
														data-target-id={`${title?.value}--edit-btn`}
													>
														{ctaLink?.value}
													</Cta>
												)}
											</S.CtaContainerEdit>
										</div>
									</>
								);
							})}
						</S.DlEdit>
					</S.Section>
				) : (
					<S.Section key={title?.value ?? ''}>
						<S.TitleContainer
							isEdit={ctaLink && isEditCtaVisible ? true : false}
						>
							<Typo.SubHeading
								tag={isHeaderH3 ? 'h3' : 'h4'}
								jss={title}
								margin="0"
							/>
							{ctaLink && isEditCtaVisible && (
								<Cta
									icon={ICONS.EDIT}
									onClick={ctaCallback}
									ctaStyle="plain"
									isWithoutPadding
									ctaType="button"
									data-target-id={`${title?.value}--edit-btn`}
								>
									{ctaLink?.value}
								</Cta>
							)}
						</S.TitleContainer>
						<S.Dl>
							{contents.map(({ label, info }, contentIndex) => {
								const isJss = isJssField(label);

								return (
									<div key={uid(`${info ?? ''}_${contentIndex}`)}>
										<dt>
											{isJss ? (
												<Typo.Body
													color="subdued"
													jss={label as JssTextType}
													margin="0"
												/>
											) : (
												<div>{label ?? ''}</div>
											)}
										</dt>
										<dd>
											{/* NOTE: use index as key for the array is static */}
											{(isArray(info) ? info : [info]).map((value, index) => (
												<Typo.Body margin="0" key={uid(index)}>
													{value ?? ''}
												</Typo.Body>
											))}
										</dd>
									</div>
								);
							})}
						</S.Dl>
					</S.Section>
				),
		)}
	</S.Wrapper>
);

export default FormReview;
